// TrendSummary.tsx
import { useCallback, useEffect, useMemo, useState } from "react";
import FilterMenu from "../../../components/FilterMenu";
import { ChartAreaLoader } from "../../../components/Loading";
import BlockRangeSelector from "../../../components/BlockRangeSelector/BlockRangeSelector";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import {
  getChhattisgarhDemandMetrics,
  getChhattisgarhForecastMetrics,
} from "../Utility/RestApi";
import { H_CHART_TYPES } from "../../Hppc/const";
import LegendSummaryTable, {
  ChartTable,
  downloadChartTableCSV,
  downloadSummaryCSV,
} from "../../../components/table/SummaryTable";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import Tooltip from "../../../components/tooltip";
import { useSelector } from "react-redux";
import { AddDatesWithFileName } from "../../../helpers/helper";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import MetricChip, { MetricChipHead } from "../../../components/Metrics";
import { ReactComponent as PeakIcon } from "../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../icons/AvgIcon.svg";
import { ReactComponent as MinIcon } from "../../../icons/MinIcon.svg";
import { ReactComponent as TotalIcon } from "../../../icons/TotalIcon.svg";
import { TrendData } from "../../../models/metrics";
// ─── Constants ────────────────────────────────────────────────────────────────

const X_AXIS_MODES = [
  { label: "Intervals", value: "intervals" },
  { label: "Peak Analysis", value: "peak" },
  { label: "Summary", value: "summary" },
];

const DISCOM_OPTIONS = ["CSPDCL"];
const UNITS = ["MW", "MWh", "LU"];

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ChhattisgarhSummaryAnalysisProps {
  onXAxisModeChange: (item: string, index: number) => void;
  selectedUnit: string;
  onUnitChange: (unit: string) => void;
  selectedDiscom: string[];
  onDiscomChange: (items: string[]) => void;
  selectedGroupKeys: string;
  onGroupByChange: (index: number) => void;
  /** selectedSuffix is the array of active suffix objects from the parent */
  selectedSuffix: Array<{
    suffix: string;
    displayName: string;
    stackSuffix: string;
  }>;
  onTypeChange: (selected: string[]) => void;
  chartSuffixes: Array<{
    suffix: string;
    displayName: string;
    stackSuffix: string;
  }>;
  metrics: TrendData | null;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function ChhattisgarhSummaryAnalysis({
  onXAxisModeChange,
  selectedUnit,
  onUnitChange,
  selectedDiscom,
  onDiscomChange,
  selectedGroupKeys,
  onGroupByChange,
  selectedSuffix, // ← driven entirely by the parent; no local override
  onTypeChange,
  chartSuffixes,
  metrics,
}: ChhattisgarhSummaryAnalysisProps) {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );
  const activeTabDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates?.[activeTab],
  );

  const [summaryData, setSummaryData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);
  const [selectedChartType, setSelectedChartType] = useState(H_CHART_TYPES[1]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // ── Fetch — fetches all date ranges, merges forecast, then aggregates by slot ─
  const fetchSummaryData = useCallback(async () => {
    if (!activeTabDates?.defaultTrendDates) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const trendDates = activeTabDates.defaultTrendDates;

      // Deduplicate by "startDate,endDate" — forecast entries share the same
      // date range as their actual counterpart (value = "start,end,Model 1")
      const seenRanges = new Set<string>();
      const uniqueTrendDates = trendDates.filter((date: any) => {
        const [start, end] = date.value.split(",");
        const rangeKey = `${start},${end}`;
        if (seenRanges.has(rangeKey)) return false;
        seenRanges.add(rangeKey);
        return true;
      });

      const promises = uniqueTrendDates.map(async (date: any) => {
        const [startDate, endDate] = date.value.split(",");

        const [raw, forecastRaw] = await Promise.all([
          getChhattisgarhDemandMetrics(startDate, endDate),
          getChhattisgarhForecastMetrics(startDate, endDate),
        ]);

        // The forecast API returns TWO entries per slot — T+1 and T+2.
        // Keep separate Maps so neither overwrites the other.
        const forecastT1BySlot = new Map<number, any>(); // D+1
        const forecastT2BySlot = new Map<number, any>(); // D+2
        (Array.isArray(forecastRaw) ? forecastRaw : []).forEach((f: any) => {
          if (f.forecast_type === "T+1") forecastT1BySlot.set(f.slot, f);
          else if (f.forecast_type === "T+2") forecastT2BySlot.set(f.slot, f);
        });

        // Index actual demand by slot for O(1) lookup
        const actualBySlot = new Map<number, any>(
          (Array.isArray(raw) ? raw : []).map((item: any) => [item.slot, item]),
        );

        // Build the union of ALL slots that appear in either API response.
        // Previously we iterated only over `raw`, so if actual demand was
        // empty (e.g. a future date) the entire result was empty even when
        // forecast data existed. Now both sources are independent:
        // — actual fields default to 0 / undefined when actual API has no data
        // — forecast fields default to 0 / undefined when forecast API has no data
        const allSlots = new Set<number>([
          ...actualBySlot.keys(),
          ...forecastT1BySlot.keys(),
          ...forecastT2BySlot.keys(),
        ]);

        return Array.from(allSlots).map((slot) => {
          const actual = actualBySlot.get(slot) ?? {};
          const fD1 = forecastT1BySlot.get(slot) ?? {};
          const fD2 = forecastT2BySlot.get(slot) ?? {};

          // time_string: prefer actual, fall back to whichever forecast has it
          const time_string =
            actual.time_string ?? fD1.time_string ?? fD2.time_string;

          return {
            slot,
            time_string,
            // ── Actual demand (empty object → all fields are 0 / undefined) ──
            min_demand: parseFloat(actual.min_demand?.toString() ?? "0"),
            max_demand: parseFloat(actual.max_demand?.toString() ?? "0"),
            avg_demand: parseFloat(actual.avg_demand?.toString() ?? "0"),
            mode_demand:
              parseFloat(
                actual.mode_demand_window_formula?.toString() ?? "0",
              ) || parseFloat(actual.mode_demand?.toString() ?? "0"),
            mode_demand_window_formula: parseFloat(
              actual.mode_demand_window_formula?.toString() ?? "0",
            ),
            date_of_min: actual.date_of_min,
            date_of_max: actual.date_of_max,
            // ── D+1 forecast ──────────────────────────────────────────────
            forecast_min_demand: parseFloat(fD1.min_demand?.toString() ?? "0"),
            forecast_max_demand: parseFloat(fD1.max_demand?.toString() ?? "0"),
            forecast_avg_demand: parseFloat(fD1.avg_demand?.toString() ?? "0"),
            forecast_mode_demand:
              parseFloat(fD1.mode_demand_window_formula?.toString() ?? "0") ||
              parseFloat(fD1.mode_demand?.toString() ?? "0"),
            forecast_date_of_min: fD1.date_of_min,
            forecast_date_of_max: fD1.date_of_max,
            // ── D+2 forecast ──────────────────────────────────────────────
            forecast_d2_min_demand: parseFloat(
              fD2.min_demand?.toString() ?? "0",
            ),
            forecast_d2_max_demand: parseFloat(
              fD2.max_demand?.toString() ?? "0",
            ),
            forecast_d2_avg_demand: parseFloat(
              fD2.avg_demand?.toString() ?? "0",
            ),
            forecast_d2_mode_demand:
              parseFloat(fD2.mode_demand_window_formula?.toString() ?? "0") ||
              parseFloat(fD2.mode_demand?.toString() ?? "0"),
            forecast_d2_date_of_min: fD2.date_of_min,
            forecast_d2_date_of_max: fD2.date_of_max,
          };
        });
      });

      // Wait for all date-range API calls to resolve
      const results = await Promise.all(promises);

      // Flatten — at this point slots 1–96 appear once per date selected,
      // e.g. 3 dates × 96 slots = 288 records. Plotting this directly causes
      // duplicate X-axis ticks (slot 1 drawn 3 times).
      const allRecords = results.flat();

      // ── Aggregate by slot across dates ───────────────────────────────────
      // Group every record that shares the same slot number, then collapse
      // the group into one summary row so the chart always shows exactly
      // 96 unique ticks regardless of how many dates are selected.
      const bySlot = new Map<number, any[]>();
      allRecords.forEach((record) => {
        const group = bySlot.get(record.slot) ?? [];
        group.push(record);
        bySlot.set(record.slot, group);
      });

      const aggregated = Array.from(bySlot.values()).map((records) => {
        const n = records.length;

        // Helper: average a numeric field across all records in the group
        const avg = (key: string) =>
          records.reduce((sum, r) => sum + (r[key] ?? 0), 0) / n;

        // For min: find the record with the lowest actual min_demand so we
        // can also surface which date produced that minimum
        const minRecord = records.reduce((best, r) =>
          (r.min_demand ?? 0) < (best.min_demand ?? 0) ? r : best,
        );

        // For max: find the record with the highest actual max_demand
        const maxRecord = records.reduce((best, r) =>
          (r.max_demand ?? 0) > (best.max_demand ?? 0) ? r : best,
        );

        return {
          // Slot identity — kept from any record (all share the same slot/time)
          slot: records[0].slot,
          time_string: records[0].time_string,

          // ── Actual demand aggregated across dates ──────────────────────
          // min: true minimum across all dates + the date it occurred
          min_demand: minRecord.min_demand,
          date_of_min: minRecord.date_of_min,
          // max: true maximum across all dates + the date it occurred
          max_demand: maxRecord.max_demand,
          date_of_max: maxRecord.date_of_max,
          // avg / mode: mean of each date's pre-computed value
          avg_demand: avg("avg_demand"),
          mode_demand_window_formula: avg("mode_demand_window_formula"),
          mode_demand: avg("mode_demand"),

          // ── D+1 forecast aggregated across dates ──────────────────────
          // Mirror actual-demand pattern: find the date-range record where
          // the D+1 min was lowest / max was highest, so the tooltip can
          // surface which date produced that extreme value.
          ...((): any => {
            const fD1Min = records.reduce((best, r) =>
              (r.forecast_min_demand ?? 0) < (best.forecast_min_demand ?? 0)
                ? r
                : best,
            );
            const fD1Max = records.reduce((best, r) =>
              (r.forecast_max_demand ?? 0) > (best.forecast_max_demand ?? 0)
                ? r
                : best,
            );
            return {
              forecast_min_demand: fD1Min.forecast_min_demand,
              forecast_date_of_min: fD1Min.forecast_date_of_min,
              forecast_max_demand: fD1Max.forecast_max_demand,
              forecast_date_of_max: fD1Max.forecast_date_of_max,
              forecast_avg_demand: avg("forecast_avg_demand"),
              forecast_mode_demand: avg("forecast_mode_demand"),
            };
          })(),

          // ── D+2 forecast aggregated across dates ──────────────────────
          ...((): any => {
            const fD2Min = records.reduce((best, r) =>
              (r.forecast_d2_min_demand ?? 0) <
              (best.forecast_d2_min_demand ?? 0)
                ? r
                : best,
            );
            const fD2Max = records.reduce((best, r) =>
              (r.forecast_d2_max_demand ?? 0) >
              (best.forecast_d2_max_demand ?? 0)
                ? r
                : best,
            );
            return {
              forecast_d2_min_demand: fD2Min.forecast_d2_min_demand,
              forecast_d2_date_of_min: fD2Min.forecast_d2_date_of_min,
              forecast_d2_max_demand: fD2Max.forecast_d2_max_demand,
              forecast_d2_date_of_max: fD2Max.forecast_d2_date_of_max,
              forecast_d2_avg_demand: avg("forecast_d2_avg_demand"),
              forecast_d2_mode_demand: avg("forecast_d2_mode_demand"),
            };
          })(),
        };
      });

      setSummaryData(aggregated);
    } catch (error) {
      console.error("Error fetching Chhattisgarh summary data:", error);
      setSummaryData([]);
    } finally {
      setLoading(false);
    }
  }, [activeTabDates]);

  useEffect(() => {
    fetchSummaryData();
  }, [fetchSummaryData]);

  // ── Chart data derivation — same pattern as reference code ────────────────
  useEffect(() => {
    // LU conversion factor added alongside MWh
    const factor =
      selectedUnit === "MWh" ? 0.25 : selectedUnit === "LU" ? 0.0025 : 1;

    const processed = summaryData.map((item) => ({
      slot: item.slot,
      slot_number: item.slot,
      slot_with_time:
        item.slot && item.time_string
          ? `${item.slot} (${item.time_string})`
          : item.slot,
      // dataKeys match legend entries exactly ↓
      min_demand: (item.min_demand ?? 0) * factor,
      max_demand: (item.max_demand ?? 0) * factor,
      avg_demand: (item.avg_demand ?? 0) * factor,
      // Prefer mode_demand_window_formula; fall back to mode_demand if zero/absent.
      // Use || not ?? — ?? only skips null/undefined, so a zero value from
      // mode_demand_window_formula would block the fallback to mode_demand.
      mode_demand:
        (item.mode_demand_window_formula || item.mode_demand || 0) * factor,
      min_demand_date: item.date_of_min,
      max_demand_date: item.date_of_max,
      // D+1 forecast — same pattern as actual demand:
      // min/max carry their occurrence date; mode uses || fallback like actual mode
      forecast_min_demand: (item.forecast_min_demand ?? 0) * factor,
      forecast_min_demand_date: item.forecast_date_of_min,
      forecast_max_demand: (item.forecast_max_demand ?? 0) * factor,
      forecast_max_demand_date: item.forecast_date_of_max,
      forecast_avg_demand: (item.forecast_avg_demand ?? 0) * factor,
      forecast_mode_demand: (item.forecast_mode_demand || 0) * factor,
      // D+2 forecast — same pattern
      forecast_d2_min_demand: (item.forecast_d2_min_demand ?? 0) * factor,
      forecast_d2_min_demand_date: item.forecast_d2_date_of_min,
      forecast_d2_max_demand: (item.forecast_d2_max_demand ?? 0) * factor,
      forecast_d2_max_demand_date: item.forecast_d2_date_of_max,
      forecast_d2_avg_demand: (item.forecast_d2_avg_demand ?? 0) * factor,
      forecast_d2_mode_demand: (item.forecast_d2_mode_demand || 0) * factor,
    }));

    processed.sort((a, b) => a.slot - b.slot);

    const filtered =
      selectedBlock.length > 0
        ? processed.filter((item) =>
            selectedBlock.map((b) => b.value).includes(item.slot),
          )
        : processed;

    setChartData(filtered);
    setTableData(filtered);
  }, [summaryData, selectedUnit, selectedBlock]);

  // ── Legends — filtered by active Type selection ───────────────────────────
  const activeSuffixKeys = useMemo(() => {
    const keys = new Set(selectedSuffix.map((s) => s.suffix));
    // D+1 and D+2 are kept as separate suffixTypes so each group of legends
    // is toggled independently by the Type filter. The parent passes
    // "demand_forecast_d1" / "demand_forecast_d2" and legends match exactly.
    return keys;
  }, [selectedSuffix]);

  const legends = useMemo(() => {
    const all = [
      // Actual Load
      {
        dataKey: "min_demand",
        name: "Min Demand",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "summary" : undefined,
        dateKey: "min_demand_date",
        suffixType: "demand",
      },
      {
        dataKey: "max_demand",
        name: "Max Demand",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "summary" : undefined,
        dateKey: "max_demand_date",
        suffixType: "demand",
      },
      {
        dataKey: "avg_demand",
        name: "Average Demand",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "summary" : undefined,
        suffixType: "demand",
      },
      {
        dataKey: "mode_demand",
        name: "Mode Demand",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "summary" : undefined,
        suffixType: "demand",
      },
      // D+1 Forecast Load — shown when Type filter includes "D+1 Forecast"
      // suffixType matches the parent's chartSuffix entry: demand_forecast_d1
      {
        dataKey: "forecast_min_demand",
        name: "Min (Forecast D+1)",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "forecast_d1" : undefined,
        dateKey: "forecast_min_demand_date",
        suffixType: "demand_forecast_d1",
      },
      {
        dataKey: "forecast_max_demand",
        name: "Max (Forecast D+1)",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "forecast_d1" : undefined,
        dateKey: "forecast_max_demand_date",
        suffixType: "demand_forecast_d1",
      },
      {
        dataKey: "forecast_avg_demand",
        name: "Average (Forecast D+1)",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "forecast_d1" : undefined,
        suffixType: "demand_forecast_d1",
      },
      {
        dataKey: "forecast_mode_demand",
        name: "Mode (Forecast D+1)",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "forecast_d1" : undefined,
        suffixType: "demand_forecast_d1",
      },
      // D+2 Forecast Load — shown when Type filter includes "D+2 Forecast"
      // suffixType matches the parent's chartSuffix entry: demand_forecast_d2
      {
        dataKey: "forecast_d2_min_demand",
        name: "Min (Forecast D+2)",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "forecast_d2" : undefined,
        dateKey: "forecast_d2_min_demand_date",
        suffixType: "demand_forecast_d2",
      },
      {
        dataKey: "forecast_d2_max_demand",
        name: "Max (Forecast D+2)",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "forecast_d2" : undefined,
        dateKey: "forecast_d2_max_demand_date",
        suffixType: "demand_forecast_d2",
      },
      {
        dataKey: "forecast_d2_avg_demand",
        name: "Average (Forecast D+2)",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "forecast_d2" : undefined,
        suffixType: "demand_forecast_d2",
      },
      {
        dataKey: "forecast_d2_mode_demand",
        name: "Mode (Forecast D+2)",
        type: selectedChartType.value,
        stackId:
          selectedChartType.stackId !== undefined ? "forecast_d2" : undefined,
        suffixType: "demand_forecast_d2",
      },
    ];
    return all.filter((l) => activeSuffixKeys.has(l.suffixType));
  }, [selectedChartType, activeSuffixKeys]);

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(legends);

  // ── Tooltip — same pattern as reference code ──────────────────────────────
  const resolveTooltipName = useCallback(
    (seriesName: string, dataPoint: any) => {
      // Actual demand — append the date the min/max occurred on.
      if (seriesName === "Min Demand" && dataPoint?.min_demand_date) {
        return `Min Demand (${dataPoint.min_demand_date})`;
      } else if (seriesName === "Max Demand" && dataPoint?.max_demand_date) {
        return `Max Demand (${dataPoint.max_demand_date})`;
      }
      // D+1 forecast
      else if (seriesName === "Min (Forecast D+1)" && dataPoint?.forecast_min_demand_date) {
        return `Min (Forecast D+1) (${dataPoint.forecast_min_demand_date})`;
      } else if (seriesName === "Max (Forecast D+1)" && dataPoint?.forecast_max_demand_date) {
        return `Max (Forecast D+1) (${dataPoint.forecast_max_demand_date})`;
      }
      // D+2 forecast
      else if (seriesName === "Min (Forecast D+2)" && dataPoint?.forecast_d2_min_demand_date) {
        return `Min (Forecast D+2) (${dataPoint.forecast_d2_min_demand_date})`;
      } else if (seriesName === "Max (Forecast D+2)" && dataPoint?.forecast_d2_max_demand_date) {
        return `Max (Forecast D+2) (${dataPoint.forecast_d2_max_demand_date})`;
      }

      return seriesName;
    },
    [],
  );

  // getSelectedTypes — keeps Type FilterMenu in sync with parent selection
  function getSelectedTypes() {
    return selectedSuffix.map((s) => s.displayName);
  }

  // ── Table props ───────────────────────────────────────────────────────────
  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData,
    defaultSortKey: "block",
    itemsPerPage: 96,
    unit: selectedUnit,
    block_key: "slot_number",
    extraKeys: ["slot_number", "Date"],
    extraLabels: ["Block", "Date"],
    filename: AddDatesWithFileName({
      fileName: "Chhattisgarh Load Trend Summary",
      dates: DefaultDates[activeTab]?.defaultRangeDate || [],
    }),
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="X-Axis"
            items={X_AXIS_MODES.map((item) => item.label)}
            selectedItems={["Summary"]}
            onSelect={onXAxisModeChange}
          />
          <FilterMenu
            title="Unit"
            items={UNITS}
            selectedItems={[selectedUnit]}
            onSelect={(item) => onUnitChange(item)}
          />
          {/* <FilterMenu
            title="Discom"
            items={DISCOM_OPTIONS}
            selectedItems={selectedDiscom}
            onSelect={() => {}}
            setSelectedItems={onDiscomChange}
          /> */}
          {/* Type filter — reflects the parent's selection via selectedSuffix */}
          <FilterMenu
            title="Type"
            items={chartSuffixes
              .filter((s) => s.suffix !== "deviation")
              .map((s) => s.displayName)}
            selectedItems={getSelectedTypes()}
            setSelectedItems={onTypeChange}
          />
          <FilterMenu
            title="Chart Type"
            items={H_CHART_TYPES.map((item) => item.label)}
            selectedItems={[selectedChartType.label]}
            onSelect={(_, index) => setSelectedChartType(H_CHART_TYPES[index])}
          />
        </div>
      </NewFilterContainer>

      <MainPageContainer>
        <SecondaryHeader />
        {metrics && (
          <div className="flex flex-wrap gap-4 my-0 justify-center">
            <MetricChipHead
              icon={<PeakIcon width={20} height={19} />}
              title="Peak Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Peak Load (MW)"].value,
                  unit: metrics.load_metrics["Peak Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Peak Forecast Load (MW)"].value,
                  unit: metrics.forecast_metrics["Peak Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<AvgIcon width={20} height={19} />}
              title="Average Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Average Load (MW)"].value,
                  unit: metrics.load_metrics["Average Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Average Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Average Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<MinIcon width={20} height={19} />}
              title="Minimum Load"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Minimum Load (MW)"].value,
                  unit: metrics.load_metrics["Minimum Load (MW)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                      .value,
                  unit: metrics.forecast_metrics["Minimum Forecast Load (MW)"]
                    .unit,
                },
              ]}
            />
            <MetricChipHead
              icon={<TotalIcon width={20} height={19} />}
              title="Total Energy"
              items={[
                {
                  label: "Actual",
                  value: metrics.load_metrics["Total Energy (MU)"].value,
                  unit: metrics.load_metrics["Total Energy (MU)"].unit,
                },
                {
                  label: "Forecast",
                  value:
                    metrics.forecast_metrics["Total Forecast Energy (MU)"]
                      .value,
                  unit: metrics.forecast_metrics["Total Forecast Energy (MU)"]
                    .unit,
                },
              ]}
            />
          </div>
        )}
        <div className="gas-chart-container">
          {loading ? (
            <ChartAreaLoader
              title="Loading Trend Summary"
              subtitle="Fetching Chhattisgarh trend summary data…"
            />
          ) : (
            <>
              <div className="gas-heading gap-x-10">
                <h2 className="chartHeading w-80">
                  Forecast Summary ({selectedUnit})
                </h2>
                <div className="flex gap-x-6 justify-end">
                  <BlockRangeSelector
                    blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                    placeholder="Search Block"
                    selectedOptions={selectedBlock.map((item) => item.value)}
                    setSelectedOptions={setSelectedBlock}
                  />
                  <button
                    onClick={() => {
                      if (dataTab.label === "Chart")
                        downloadChartTableCSV(tablePar);
                      else if (dataTab.label === "Summary")
                        downloadSummaryCSV(tablePar);
                      else if (dataTab.label === "Table")
                        downloadChartTableCSV(tablePar);
                    }}
                    className="downloadButton"
                  >
                    Download Data
                  </button>
                </div>
              </div>

              {dataTab.label === "Chart" && (
                <ReMixChart
                  key={`${selectedChartType.value}-${selectedUnit}`}
                  data={chartData}
                  showBrush={true}
                  isTimeSlot={true}
                  onlyTitle={true}
                  barCategoryGap={10}
                  unit={selectedUnit}
                  yAxisLabel={selectedUnit}
                  legends={legends as any}
                  onLegendSelect={handleLegendSelect}
                  defaultSelectedLegends={selectedLegends}
                  xDataKey="slot"
                  secondXDataKey={undefined}
                  resolveDisplayName={resolveTooltipName}
                />
              )}
              {dataTab.label === "Summary" && (
                <LegendSummaryTable {...tablePar} />
              )}
              {dataTab.label === "Table" && (
                <ChartTable {...tablePar} defaultSortDirection="asc" />
              )}
            </>
          )}
        </div>

        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5">
            <Tooltip
              note={<p>CSPDCL</p>}
              text="
           1. Actual Load data is sourced from CSPDCL's official records.
           2. Forecast Load data is provided by GNA Energy."
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
}
